import { useEffect, useRef, useCallback } from 'react'
import { useNotificationStore } from '@/stores/notificationStore'

interface Options {
  companyId?: number
}

export const useNotificationWebSocket = ({ companyId }: Options) => {
  const addNotification = useNotificationStore((s) => s.addNotification)
  const wsRef = useRef<WebSocket | null>(null)
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(true)

  const connect = useCallback(() => {
    if (!companyId || companyId <= 0) return
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const sessionId = `sess_notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws/chat/${sessionId}`)
    wsRef.current = ws

    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: 'handshake',
        conversation_id: `notifications_${companyId}`,
        company_id: companyId,
        timestamp: new Date().toISOString(),
      }))
      heartbeatRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'heartbeat', timestamp: new Date().toISOString() }))
        }
      }, 30000)
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        if (data.type === 'notification') {
          addNotification(data.data)
        }
      } catch { /* ignore */ }
    }

    ws.onclose = () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current)
      if (isMountedRef.current && !reconnectRef.current) {
        reconnectRef.current = setTimeout(() => {
          reconnectRef.current = null
          if (isMountedRef.current) connect()
        }, 5000)
      }
    }

    ws.onerror = () => { /* onclose will fire */ }
  }, [companyId, addNotification])

  useEffect(() => {
    isMountedRef.current = true
    connect()
    return () => {
      isMountedRef.current = false
      if (reconnectRef.current) clearTimeout(reconnectRef.current)
      if (heartbeatRef.current) clearInterval(heartbeatRef.current)
      if (wsRef.current) {
        wsRef.current.close(1000)
        wsRef.current = null
      }
    }
  }, [connect])
}
